import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_ui } from '@/hooks/store/use_slices';
import { G_CONTEXT_MENU__field } from '@/pages/page_generation/sidebar/_base/context_menu/G_CONTEXT_MENU__field';
import { LOCAL__ui_put_set_of_strings } from '@/state/ui/_base/LOCAL__ui_put_set_of_strings';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { Cell, ColumnDef, flexRender, getCoreRowModel, Header, Row, useReactTable } from '@tanstack/react-table';

interface PROPS<T extends FIELD__base> {
  fields: T[];
  columns: ColumnDef<T, any>[];
  title: string;
}

export function G_TABLE__inputs__base<T extends FIELD__base>({ fields, columns, title }: PROPS<T>) {
  const dispatch = use_dispatch();
  const { g_inputs__fields_ids_selected } = use_slice_ui(['g_inputs__fields_ids_selected']);

  /*
   *
   */

  const table = useReactTable({ data: fields, columns, getCoreRowModel: getCoreRowModel() });

  /*
   *
   */

  function on_header_clicked() {
    const table_field_ids = fields.map(field => field.meta.field_id);
    const table_field_set = new Set(table_field_ids);

    // Check if every field in this table is currently selected
    const is_all_selected = table_field_ids.every(id => g_inputs__fields_ids_selected.has(id));

    let value_new: Set<string>;

    if (!is_all_selected) {
      // Select all: Merge current global selections with this table's fields
      value_new = new Set([...g_inputs__fields_ids_selected, ...table_field_ids]);
    } else {
      // Deselect all: Keep only items NOT belonging to this table
      value_new = new Set([...g_inputs__fields_ids_selected].filter(id => !table_field_set.has(id)));
    }

    dispatch(LOCAL__ui_put_set_of_strings({ key: 'g_inputs__fields_ids_selected', value: value_new }));
  }

  function on_selected__field(field: T) {
    const fields_ids_new = new Set(g_inputs__fields_ids_selected);

    fields_ids_new.has(field.meta.field_id) ? fields_ids_new.delete(field.meta.field_id) : fields_ids_new.add(field.meta.field_id);

    dispatch(LOCAL__ui_put_set_of_strings({ key: 'g_inputs__fields_ids_selected', value: fields_ids_new }));
  }

  /*
   *
   */

  function render_title() {
    const selected_amount = fields.filter(field => g_inputs__fields_ids_selected.has(field.meta.field_id)).length;

    const text_amount = selected_amount > 0 ? `${selected_amount}/${fields.length}` : `${fields.length}`;

    return (
      <div onClick={on_header_clicked} className="flex items-center gap-2 px-1 w-full h-6 bg-bar sticky top-0 z-10 cursor-pointer">
        <span className="text-xs font-semibold tracking-wider text-bar-foreground uppercase">{title}</span>
        <span className="px-1.5 text-xs font-medium rounded-sm ring-1 ring-component-border text-bar-foreground">{text_amount}</span>
      </div>
    );
  }

  function get_column_width_class(column_id: string): string {
    const class_name_map = new Map([
      ['family', 'w-12 '],
      ['node_type', 'w-48 '],
      ['role', 'w-48 '],
      ['field_kind', 'w-20 '],
      ['actions', 'w-60'],
      ['model_type', 'w-20 '],
      ['items_amount', 'w-28 justify-end'],
      ['model_value', 'w-32 '],
    ]);

    return class_name_map.get(column_id) ?? 'min-w-0';
  }

  function render_cell_header(header_cell: Header<T, unknown>) {
    const width_class = get_column_width_class(header_cell.column.id);

    return (
      <div key={header_cell.id} className={`px-1 h-6 flex items-center whitespace-nowrap truncate shrink-0 ${width_class}`}>
        {flexRender(header_cell.column.columnDef.header, header_cell.getContext())}
      </div>
    );
  }

  function render_row(row: Row<T>) {
    const is_selected = g_inputs__fields_ids_selected.has(row.original.meta.field_id);

    return (
      <div
        onClick={() => on_selected__field(row.original)}
        key={row.id}
        className={`flex items-center text-xs h-6 border-b border-component-border cursor-pointer
          ${is_selected ? 'bg-component-item-selected text-component-item-foreground' : 'text-component-foreground'} 
          hover:bg-component-item-selected hover:text-component-item-foreground transition-colors`}
      >
        {row.getVisibleCells().map(cell => render_cell(row, cell))}
      </div>
    );
  }

  function render_cell<T extends FIELD__base>(row: Row<T>, cell: Cell<T, unknown>) {
    const width_class = get_column_width_class(cell.column.id);

    return (
      <div
        key={cell.id}
        className={`px-1 h-full flex items-center whitespace-nowrap truncate border-r border-dashed border-component-border shrink-0 ${width_class}`}
      >
        <G_CONTEXT_MENU__field field={row.original}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</G_CONTEXT_MENU__field>
      </div>
    );
  }

  /*
   *
   */

  if (fields.is_empty()) return null;

  return (
    <div className="w-full overflow-visible flex flex-col">
      {render_title()}
      <div className="w-full flex flex-col">
        {/* Header List */}
        <div
          onClick={on_header_clicked}
          className="sticky top-6 z-10 flex items-center text-xs uppercase text-bar-foreground bg-bar border-b border-component-border cursor-pointer"
        >
          {table.getHeaderGroups().map(headerGroup => (
            <div key={headerGroup.id} className="flex w-full items-center">
              {headerGroup.headers.map(render_cell_header)}
            </div>
          ))}
        </div>

        {/* Rows List */}
        <div className="flex flex-col border-b border-component-border">{table.getRowModel().rows.map(render_row)}</div>
      </div>
    </div>
  );
}
